import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    Sparkles, ArrowRight, ArrowLeft, RotateCcw, Send, Loader2, Star,
    Crown, Clock, Wind, Droplets, Sun, Heart, Leaf, Shield, Zap,
    CheckCircle, X,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const LOGO_URL = "/brand/24k-logo.jpg";

// ─────────────────────────── Quiz content (ported from Base44) ───────────────────────────
const QUESTIONS = [
    {
        id: 1,
        question: "How does your skin feel a few hours after cleansing?",
        options: [
            { text: "Tight and uncomfortable", type: "dry" },
            { text: "Shiny all over", type: "oily" },
            { text: "Oily in T-zone, dry on cheeks", type: "combination" },
            { text: "Comfortable and balanced", type: "normal" },
            { text: "Irritated or red", type: "sensitive" },
        ],
    },
    {
        id: 2,
        question: "How often do you experience breakouts?",
        options: [
            { text: "Rarely or never", type: "dry" },
            { text: "Frequently, especially on forehead and nose", type: "oily" },
            { text: "Occasionally, mostly in T-zone", type: "combination" },
            { text: "Sometimes, but they clear quickly", type: "normal" },
            { text: "Often, with redness and irritation", type: "sensitive" },
        ],
    },
    {
        id: 3,
        question: "How does your skin react to new products?",
        options: [
            { text: "Often feels even drier", type: "dry" },
            { text: "Gets more oily or breaks out", type: "oily" },
            { text: "Depends on the area of my face", type: "combination" },
            { text: "Usually adapts well", type: "normal" },
            { text: "Often stings, burns, or turns red", type: "sensitive" },
        ],
    },
    {
        id: 4,
        question: "What best describes your pores?",
        options: [
            { text: "Small and barely visible", type: "dry" },
            { text: "Large and visible all over", type: "oily" },
            { text: "Larger on nose, smaller on cheeks", type: "combination" },
            { text: "Average size, not very noticeable", type: "normal" },
            { text: "Can appear larger when irritated", type: "sensitive" },
        ],
    },
    {
        id: 5,
        question: "How does your skin feel in different seasons?",
        options: [
            { text: "Worse in winter, very dry and flaky", type: "dry" },
            { text: "Oily year-round, worse in summer", type: "oily" },
            { text: "Changes with the seasons", type: "combination" },
            { text: "Stays pretty consistent", type: "normal" },
            { text: "Reacts to weather changes with redness", type: "sensitive" },
        ],
    },
];

const SKIN_RESULTS = {
    dry: {
        title: "Dry Skin",
        icon: Wind,
        description: "Your skin needs extra hydration and nourishment to restore its natural glow.",
        tips: {
            hydrate: "Use a rich, cream-based moisturizer morning and night. Look for hyaluronic acid and ceramides to lock in moisture.",
            cleanse: "Choose a gentle, hydrating cleanser. Avoid foaming cleansers that strip natural oils.",
            protect: "Always use SPF 30+ sunscreen. Sun damage worsens dryness and causes premature aging.",
            treat: "Add a hydrating serum with vitamin E or squalane. Use a sleeping mask weekly for deep nourishment.",
        },
    },
    oily: {
        title: "Oily Skin",
        icon: Droplets,
        description: "Your skin produces extra oil, but with the right care, you can achieve a balanced, healthy glow.",
        tips: {
            hydrate: "Use a lightweight, oil-free gel moisturizer. Skipping moisturizer actually makes oiliness worse!",
            cleanse: "Use a gentle foaming cleanser twice daily. Don't over-wash — it triggers more oil production.",
            protect: "Choose a mattifying SPF 30+ sunscreen. Look for 'non-comedogenic' on the label.",
            treat: "Use a clay mask weekly to absorb excess oil. Niacinamide serums help control shine.",
        },
    },
    combination: {
        title: "Combination Skin",
        icon: Sun,
        description: "Your skin has different needs in different areas — we'll help you find the perfect balance.",
        tips: {
            hydrate: "Use a lightweight moisturizer all over, add richer cream to dry areas only.",
            cleanse: "Use a balanced gel cleanser. Multi-mask: clay on T-zone, hydrating on cheeks.",
            protect: "Use a lightweight SPF 30+ that won't clog pores or feel heavy.",
            treat: "Balance with niacinamide serum. Exfoliate gently 1–2x weekly to keep pores clear.",
        },
    },
    normal: {
        title: "Normal Skin",
        icon: Heart,
        description: "Your skin is beautifully balanced! Focus on maintenance and prevention.",
        tips: {
            hydrate: "A medium-weight moisturizer keeps your skin happy. Don't skip this step!",
            cleanse: "Any gentle cleanser works for you. Stick with what feels good.",
            protect: "SPF 30+ daily is your anti-aging secret weapon. Rain or shine!",
            treat: "Add antioxidant serums like vitamin C to maintain your glow and prevent aging.",
        },
    },
    sensitive: {
        title: "Sensitive Skin",
        icon: Leaf,
        description: "Your skin needs gentle, soothing care with minimal ingredients.",
        tips: {
            hydrate: "Use fragrance-free, hypoallergenic moisturizers. Look for calming ingredients like aloe.",
            cleanse: "Cream or milk cleansers are gentlest. Avoid anything with fragrance or alcohol.",
            protect: "Mineral SPF (zinc oxide) is gentler than chemical sunscreens for sensitive skin.",
            treat: "Centella asiatica and chamomile calm irritation. Always patch test new products!",
        },
    },
};

// Maps each skin type → one of the existing 24K Glow facials
const FACIAL_FOR_SKIN = {
    dry: {
        id: "facial_hydrating",
        title: "Hydrating Facial",
        duration: 60,
        desc: "Vitamin C serum, lifting massage, peptide mask — restores moisture, fades dullness, plumps fine lines. Built around dry-skin chemistry.",
        ingredients: ["Hyaluronic acid", "Vitamin C", "Peptides", "Squalane"],
    },
    oily: {
        id: "facial_signature",
        title: "Signature Facial",
        duration: 60,
        desc: "Salicylic clarifying steam, extractions, blue-light healing, and a calming gel mask — decongests pores and balances oil without stripping.",
        ingredients: ["Salicylic acid", "Niacinamide", "Tea tree", "Blue light therapy"],
    },
    combination: {
        id: "facial_signature",
        title: "Signature Facial",
        duration: 45,
        desc: "Deep cleanse, exfoliation, custom mask, hydration. Multi-zone protocol balances your T-zone while nourishing the cheeks.",
        ingredients: ["Lactic acid", "Niacinamide", "Hyaluronic acid", "Green tea"],
    },
    normal: {
        id: "facial_signature",
        title: "Signature Facial",
        duration: 45,
        desc: "Your monthly maintenance ritual — cleanse, exfoliate, custom mask, hydrate. Keeps your already-balanced skin glowing.",
        ingredients: ["Vitamin C", "Hyaluronic acid", "Antioxidants", "Custom mask"],
    },
    sensitive: {
        id: "facial_hydrating",
        title: "Hydrating Facial (Gentle Protocol)",
        duration: 60,
        desc: "A calming, fragrance-free version of our treatment — chamomile steam, mineral mask, and zinc-based finishing. Designed for reactive skin.",
        ingredients: ["Centella asiatica", "Chamomile", "Zinc oxide", "Aloe vera"],
    },
};

const TIP_BUTTONS = [
    { key: "hydrate", label: "Hydration", icon: Droplets },
    { key: "cleanse", label: "Cleansing", icon: Sparkles },
    { key: "protect", label: "Protection", icon: Shield },
    { key: "treat", label: "Treatment", icon: Zap },
];

// ─────────────────────────── Component ───────────────────────────
export default function SkinQuiz() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const [step, setStep] = useState("info"); // info | quiz | results | submitted
    const [currentQ, setCurrentQ] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [clientInfo, setClientInfo] = useState({
        name: user?.name || "",
        email: user?.email || "",
        phone: "",
    });
    const [selectedTip, setSelectedTip] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const skinType = useMemo(() => {
        if (answers.length < QUESTIONS.length) return null;
        const counts = answers.reduce((acc, t) => {
            acc[t] = (acc[t] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
    }, [answers]);

    const result = skinType ? SKIN_RESULTS[skinType] : null;
    const facial = skinType ? FACIAL_FOR_SKIN[skinType] : null;

    const progress = step === "quiz" ? (currentQ / QUESTIONS.length) * 100 : 0;

    const startQuiz = () => {
        if (!clientInfo.name.trim() || !clientInfo.email.trim()) {
            toast.error("Please enter your name and email");
            return;
        }
        setStep("quiz");
    };

    const handleAnswer = (type) => {
        const next = [...answers, type];
        setAnswers(next);
        if (currentQ < QUESTIONS.length - 1) {
            setCurrentQ(currentQ + 1);
        } else {
            setStep("results");
        }
    };

    const goBack = () => {
        if (currentQ === 0) {
            setStep("info");
            setAnswers([]);
        } else {
            setCurrentQ(currentQ - 1);
            setAnswers(answers.slice(0, -1));
        }
    };

    const submitAssessment = async () => {
        setIsSubmitting(true);
        try {
            await api.post("/skin-assessment", {
                client_name: clientInfo.name,
                client_email: clientInfo.email.toLowerCase().trim(),
                client_phone: clientInfo.phone || null,
                skin_type: skinType,
                answers,
                recommended_facial: facial?.title,
                source: (searchParams.get("src") || searchParams.get("utm_source") || "direct")
                    .toLowerCase()
                    .slice(0, 40),
            });
            setStep("submitted");
        } catch (err) {
            console.error("submit assessment failed:", err);
            // Still show submitted state — we'd rather not block the UX on a save failure
            setStep("submitted");
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetQuiz = () => {
        setStep("info");
        setCurrentQ(0);
        setAnswers([]);
        setSelectedTip(null);
    };

    const bookFacial = () => {
        navigate(`/portal/book?service=${facial?.id || "facial_signature"}&match=${skinType}`);
    };

    return (
        <div className="bg-ink text-white relative overflow-hidden bg-grain min-h-screen">
            <Navbar />

            {/* Soft glow blobs */}
            <div className="pointer-events-none absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-rose/15 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-gold/15 blur-[120px]" />

            <main className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 pt-32 pb-20">
                {/* Header */}
                <div className="text-center mb-10">
                    <div className="relative inline-block mb-6">
                        <div className="absolute -inset-2 rounded-full bg-gold/30 blur-2xl animate-pulse" />
                        <img
                            src={LOGO_URL}
                            alt="24K Glow On The Go"
                            className="relative w-24 h-24 rounded-full object-cover ring-2 ring-gold/60 shadow-2xl"
                        />
                    </div>
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <Star className="w-3.5 h-3.5 text-gold fill-gold" />
                        <span className="text-gold/80 text-xs uppercase tracking-[0.3em]">Mobile Luxury Spa</span>
                        <Star className="w-3.5 h-3.5 text-gold fill-gold" />
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl tracking-wide">
                        <span className="text-gold">24K</span> Glow On The Go
                    </h1>
                    <div className="h-px w-full max-w-xs mx-auto mt-6 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />

                    <div className="mt-8">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gold/15 border border-gold/30 mb-4">
                            <Leaf className="w-7 h-7 text-gold" />
                        </div>
                        <h2 className="font-serif text-2xl sm:text-3xl">
                            Discover Your <span className="italic text-rose">Skin Type</span>
                        </h2>
                        <p className="text-white/55 text-sm mt-2 max-w-sm mx-auto">
                            Take our 2-minute assessment and unlock your perfect facial.
                        </p>
                    </div>
                </div>

                {/* ── INFO STEP ─────────────────────── */}
                {step === "info" && (
                    <div data-testid="quiz-info" className="rounded-3xl border border-white/10 bg-surface/80 backdrop-blur-xl overflow-hidden">
                        <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
                        <div className="p-8 sm:p-10">
                            <div className="flex justify-center mb-5">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <Star key={i} className="w-4 h-4 text-gold fill-gold" />
                                ))}
                            </div>
                            <h3 className="font-serif text-2xl text-center">Begin Your Journey</h3>
                            <p className="text-white/55 text-center text-sm mt-2 mb-8">
                                Enter your details for personalized recommendations
                            </p>

                            <div className="space-y-4">
                                <FormField
                                    id="quiz-name"
                                    label="Full Name"
                                    value={clientInfo.name}
                                    onChange={(v) => setClientInfo({ ...clientInfo, name: v })}
                                    placeholder="Enter your name"
                                />
                                <FormField
                                    id="quiz-email"
                                    label="Email Address"
                                    value={clientInfo.email}
                                    onChange={(v) => setClientInfo({ ...clientInfo, email: v })}
                                    placeholder="your@email.com"
                                    type="email"
                                />
                                <FormField
                                    id="quiz-phone"
                                    label="Phone (Optional)"
                                    value={clientInfo.phone}
                                    onChange={(v) => setClientInfo({ ...clientInfo, phone: v })}
                                    placeholder="(555) 123-4567"
                                    type="tel"
                                />

                                <button
                                    data-testid="quiz-start-btn"
                                    onClick={startQuiz}
                                    disabled={!clientInfo.name.trim() || !clientInfo.email.trim()}
                                    className="mt-4 w-full h-14 rounded-2xl bg-gradient-to-r from-gold via-rose to-gold text-ink font-semibold shadow-xl shadow-gold/20 hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    Start Assessment
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                                <p className="text-center text-white/40 text-xs">
                                    Takes only 2 minutes · 100% personalized
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── QUIZ STEP ─────────────────────── */}
                {step === "quiz" && (
                    <div data-testid="quiz-questions">
                        <div className="mb-8">
                            <div className="flex justify-between text-xs text-white/55 mb-2">
                                <span>Question {currentQ + 1} of {QUESTIONS.length}</span>
                                <span>{Math.round(progress)}%</span>
                            </div>
                            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-gold to-rose transition-all duration-500"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>

                        <div className="rounded-3xl border border-white/10 bg-surface/80 backdrop-blur-xl overflow-hidden">
                            <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
                            <div className="p-8 sm:p-10">
                                <p className="text-center text-gold/70 text-xs uppercase tracking-[0.3em] mb-4">
                                    Question {currentQ + 1}
                                </p>
                                <h2 className="font-serif text-xl sm:text-2xl leading-relaxed text-center mb-8">
                                    {QUESTIONS[currentQ].question}
                                </h2>

                                <div className="space-y-3">
                                    {QUESTIONS[currentQ].options.map((opt, idx) => (
                                        <button
                                            key={opt.text}
                                            data-testid={`quiz-option-${idx}`}
                                            onClick={() => handleAnswer(opt.type)}
                                            className="group w-full text-left p-5 rounded-2xl bg-ink/40 border border-white/10 hover:border-gold/50 hover:bg-ink/60 transition flex items-center gap-3"
                                        >
                                            <span className="w-8 h-8 rounded-full border border-white/15 bg-white/5 text-white/55 group-hover:bg-gold/15 group-hover:text-gold group-hover:border-gold/40 flex items-center justify-center text-xs font-medium transition shrink-0">
                                                {String.fromCharCode(65 + idx)}
                                            </span>
                                            <span className="text-sm sm:text-base text-white/80 group-hover:text-white transition">
                                                {opt.text}
                                            </span>
                                        </button>
                                    ))}
                                </div>

                                <div className="mt-8 flex items-center justify-between text-sm">
                                    <button
                                        data-testid="quiz-back-btn"
                                        onClick={goBack}
                                        className="text-white/50 hover:text-white transition flex items-center gap-2"
                                    >
                                        <ArrowLeft className="w-4 h-4" /> Back
                                    </button>
                                    <button
                                        onClick={resetQuiz}
                                        className="text-white/40 hover:text-rose transition flex items-center gap-1.5 text-xs"
                                    >
                                        <X className="w-3 h-3" /> Restart
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── RESULTS STEP ─────────────────────── */}
                {step === "results" && result && facial && (
                    <div data-testid="quiz-results" className="space-y-6">
                        {/* Skin type card */}
                        <div className="rounded-3xl border border-gold/30 bg-gradient-to-br from-surface via-surface/90 to-ink/50 overflow-hidden">
                            <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
                            <div className="p-8 sm:p-10 text-center">
                                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/20 border border-gold/40 mb-5">
                                    <result.icon className="w-10 h-10 text-gold" />
                                </div>
                                <p className="text-gold/70 text-xs uppercase tracking-[0.3em]">Your result</p>
                                <h2 className="font-serif text-4xl sm:text-5xl mt-2">{result.title}</h2>
                                <p className="text-white/70 mt-4 leading-relaxed max-w-md mx-auto">
                                    {result.description}
                                </p>
                            </div>
                        </div>

                        {/* Personalized tips */}
                        <div className="rounded-3xl border border-white/10 bg-surface/80 backdrop-blur-xl overflow-hidden">
                            <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
                            <div className="p-6 sm:p-8">
                                <p className="text-gold/70 text-xs uppercase tracking-[0.3em] text-center mb-1">Your Personalized Guide</p>
                                <h3 className="font-serif text-xl text-center">Tap to explore your routine</h3>

                                <div className="mt-6 grid grid-cols-2 gap-3">
                                    {TIP_BUTTONS.map((tip) => {
                                        const active = selectedTip === tip.key;
                                        return (
                                            <button
                                                key={tip.key}
                                                data-testid={`tip-${tip.key}`}
                                                onClick={() => setSelectedTip(active ? null : tip.key)}
                                                className={`p-4 rounded-2xl flex flex-col items-center gap-2 transition ${
                                                    active
                                                        ? "bg-gradient-to-br from-gold/25 to-rose/15 border border-gold/50"
                                                        : "bg-ink/40 border border-white/10 hover:border-white/25"
                                                }`}
                                            >
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-gold/30" : "bg-white/5"}`}>
                                                    <tip.icon className={`w-5 h-5 ${active ? "text-gold" : "text-white/60"}`} />
                                                </div>
                                                <span className={`text-sm font-medium ${active ? "text-white" : "text-white/65"}`}>
                                                    {tip.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {selectedTip ? (
                                    <div className="mt-5 rounded-2xl border border-white/10 bg-ink/40 p-5">
                                        <p className="text-white/80 leading-relaxed text-sm sm:text-base">
                                            {result.tips[selectedTip]}
                                        </p>
                                    </div>
                                ) : (
                                    <p className="mt-5 text-center text-white/40 text-sm">
                                        Select a category above to see your personalized tips
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Recommended facial card */}
                        <div className="relative rounded-3xl overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-gold via-rose to-gold rounded-3xl" />
                            <div className="absolute inset-[2px] bg-gradient-to-br from-surface to-ink rounded-[22px]" />
                            <div className="relative p-8 sm:p-10">
                                <div className="flex items-center gap-4 mb-5">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gold to-rose flex items-center justify-center shadow-lg shadow-gold/30">
                                        <Crown className="w-7 h-7 text-ink" />
                                    </div>
                                    <div>
                                        <p className="text-gold text-[10px] uppercase tracking-[0.3em] mb-1">
                                            Tailored for your {skinType} skin
                                        </p>
                                        <h3 className="font-serif text-2xl">{facial.title}</h3>
                                    </div>
                                </div>

                                <div className="bg-ink/60 rounded-2xl p-5 mb-5 border border-gold/20">
                                    <p className="text-white/80 leading-relaxed">{facial.desc}</p>
                                </div>

                                <div className="mb-6">
                                    <p className="text-white/50 text-xs uppercase tracking-widest mb-3">Key Ingredients</p>
                                    <div className="flex flex-wrap gap-2">
                                        {facial.ingredients.map((ing) => (
                                            <span
                                                key={ing}
                                                className="px-3 py-1.5 bg-gold/10 border border-gold/30 rounded-full text-xs text-gold"
                                            >
                                                ✦ {ing}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-rose/30 bg-rose/5 p-5 mb-6 flex items-center justify-between">
                                    <div>
                                        <p className="text-white/50 text-xs uppercase tracking-widest mb-1">Duration</p>
                                        <p className="font-serif text-3xl flex items-center gap-2">
                                            <Clock className="w-5 h-5 text-gold" />
                                            {facial.duration} <span className="text-white/55 text-base">min</span>
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-white/50 text-xs uppercase tracking-widest mb-1">Mobile</p>
                                        <p className="font-serif text-base text-gold">At your door</p>
                                    </div>
                                </div>

                                <button
                                    data-testid="quiz-book-btn"
                                    onClick={bookFacial}
                                    className="w-full h-14 rounded-2xl bg-gradient-to-r from-gold via-rose to-gold text-ink font-semibold text-base shadow-xl shadow-gold/25 hover:opacity-90 transition flex items-center justify-center gap-2"
                                >
                                    Book This Facial
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Save / retake */}
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                data-testid="quiz-email-btn"
                                onClick={submitAssessment}
                                disabled={isSubmitting}
                                className="flex-1 h-12 rounded-2xl border border-white/15 bg-ink/40 text-white hover:bg-ink/60 transition flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isSubmitting ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                                ) : (
                                    <><Send className="w-4 h-4" /> Save & Email Me My Results</>
                                )}
                            </button>
                            <button
                                onClick={resetQuiz}
                                className="h-12 px-5 rounded-2xl border border-white/10 text-white/60 hover:text-white hover:border-white/25 transition flex items-center justify-center gap-2"
                            >
                                <RotateCcw className="w-4 h-4" /> Retake
                            </button>
                        </div>
                    </div>
                )}

                {/* ── SUBMITTED STEP ─────────────────────── */}
                {step === "submitted" && (
                    <div data-testid="quiz-submitted" className="rounded-3xl border border-gold/30 bg-gradient-to-br from-rose/10 to-gold/10 backdrop-blur-xl p-8 sm:p-10 text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/20 border border-gold/40 mb-6">
                            <CheckCircle className="w-10 h-10 text-gold" />
                        </div>
                        <h2 className="font-serif text-3xl sm:text-4xl">Assessment complete ✦</h2>
                        <p className="text-white/70 mt-4 leading-relaxed max-w-md mx-auto">
                            Thank you, {clientInfo.name.split(" ")[0]}! Your results are saved. We&apos;ll be in touch at <span className="text-gold">{clientInfo.email}</span>.
                        </p>

                        <div className="mt-8 space-y-3">
                            <button
                                onClick={bookFacial}
                                className="w-full h-14 rounded-2xl bg-gradient-to-r from-gold via-rose to-gold text-ink font-semibold shadow-xl shadow-gold/25 hover:opacity-90 transition flex items-center justify-center gap-2"
                            >
                                Book Your Facial Now <ArrowRight className="w-5 h-5" />
                            </button>
                            <button
                                onClick={resetQuiz}
                                className="w-full h-12 rounded-2xl border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition flex items-center justify-center gap-2"
                            >
                                <RotateCcw className="w-4 h-4" /> Take Another Assessment
                            </button>
                            <Link
                                to="/"
                                className="block text-center text-white/40 hover:text-white/70 text-sm mt-4 transition"
                            >
                                Back to home
                            </Link>
                        </div>
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
}

function FormField({ id, label, value, onChange, placeholder, type = "text" }) {
    return (
        <div>
            <label htmlFor={id} className="block text-white/55 text-[11px] uppercase tracking-[0.25em] mb-2">
                {label}
            </label>
            <input
                id={id}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={id}
                className="w-full h-14 rounded-2xl bg-ink/50 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/20 px-4 outline-none transition placeholder:text-white/30"
            />
        </div>
    );
}
